import { Injectable, computed, signal } from '@angular/core';

/**
 * A face the user picked in the CAD editor to tell the assistant where to edit.
 * `point` (the hit point) and `normal` are in the body's own model coordinates (mm), the
 * frame the body's script and STEP use, so the agent can trace the face to its feature.
 */
export interface AssistantFacePick {
  shapeId: string;
  shapeName: string;
  faceIndex: number;
  point: [number, number, number];
  normal: [number, number, number];
}

/**
 * Faces picked for the assistant: the CAD toolbar adds and removes them, the chat sends them
 * with the next message (as extraData.cadSelection) and then clears them.
 */
@Injectable({ providedIn: 'root' })
export class AssistantFaceSelectionService {
  readonly picks = signal<AssistantFacePick[]>([]);
  readonly count = computed(() => this.picks().length);

  /** Adds the face, or removes it when it is already picked. */
  toggle(pick: AssistantFacePick): void {
    const current = this.picks();
    const same = (p: AssistantFacePick) => p.shapeId === pick.shapeId && p.faceIndex === pick.faceIndex;
    this.picks.set(current.some(same) ? current.filter((p) => !same(p)) : [...current, pick]);
  }

  clear(): void {
    if (this.picks().length) this.picks.set([]);
  }

  /** The picks for the message being sent; clears them so each pick is sent once. */
  take(): AssistantFacePick[] {
    const picks = this.picks();
    this.clear();
    return picks;
  }
}
